import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Upload, Check, Wallet, Sparkles, History } from "lucide-react";
import { useAccounts } from "../lib/accounts";
import HelpButton from "../components/HelpButton";

const AUTO_CLEAN_KEY = "syncbook_auto_clean_import";

export default function Import() {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [applyResult, setApplyResult] = useState(null); // {matched, created}
  const nav = useNavigate();
  const { accounts, selectedId } = useAccounts();
  const [targetAccountId, setTargetAccountId] = useState("");
  const [autoClean, setAutoClean] = useState(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(AUTO_CLEAN_KEY) : null;
    return stored === null ? true : stored === "1";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(AUTO_CLEAN_KEY, autoClean ? "1" : "0");
    }
  }, [autoClean]);

  // Default the import target to the currently-selected account
  useEffect(() => {
    if (!targetAccountId && (selectedId || accounts[0]?.id)) {
      setTargetAccountId(selectedId || accounts[0].id);
    }
  }, [selectedId, accounts, targetAccountId]);

  const targetAccount = accounts.find((a) => a.id === targetAccountId);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      const parsed = await api.post("/import/parse", {
        csv_text: text,
        account_id: targetAccountId || undefined,
      });
      const parsedRows = parsed.data.rows;

      // Auto-apply: bank CSV is source of truth.
      //   - matched uncleared txns → mark cleared
      //   - duplicate (already in register as cleared) → skip (no-op, already there)
      //   - everything else → add new (created cleared by /import/apply)
      const items = parsedRows.map((r) => ({
        date: r.date,
        description: r.suggested_payee || r.description,
        amount: r.amount,
        type: r.type,
        action: r.duplicate ? "skip" : r.match_txn_id ? "match" : "new",
        match_txn_id: r.match_txn_id,
        category:
          r.suggested_category ||
          (r.type === "deposit" ? "Income" : "Other"),
      }));
      const applied = await api.post("/import/apply", {
        items,
        account_id: targetAccountId || undefined,
        filename: f.name,
      });
      setRows(parsedRows);
      setApplyResult(applied.data);
      const dupMsg = parsed.data.duplicates
        ? ` · ${parsed.data.duplicates} already in register`
        : "";
      toast.success(
        `${applied.data.matched} cleared · ${applied.data.created} added${dupMsg}`
      );
      if (autoClean && applied.data.created > 0 && targetAccountId) {
        runAutoClean(targetAccountId);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Check CSV format.");
    } finally {
      setBusy(false);
    }
  };

  const runAutoClean = async (accountId) => {
    if (!accountId) return;
    try {
      const { data } = await api.get(`/duplicates?account_id=${encodeURIComponent(accountId)}&fuzzy=1`);
      const groups = data?.groups || [];
      const extras = groups.flatMap((g) => g.transactions.slice(1).map((t) => t.id));
      if (extras.length === 0) {
        toast.success("Auto-clean: no duplicates found");
        return;
      }
      toast(`${extras.length} duplicate${extras.length === 1 ? "" : "s"} detected`, {
        description: "Kept the oldest copy in each group. Tap to merge the rest.",
        duration: 12000,
        action: {
          label: `Merge ${extras.length}`,
          onClick: async () => {
            try {
              const { data: res } = await api.post("/transactions/bulk-delete", { ids: extras });
              toast.success(`Merged ${res.deleted} duplicate${res.deleted === 1 ? "" : "s"}`);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Auto-clean failed");
            }
          },
        },
      });
    } catch (err) {
      // Silent: auto-clean is best-effort and shouldn't block the happy import path
      console.warn("Auto-clean skipped:", err?.response?.data?.detail || err?.message);
    }
  };

  const reset = () => {
    setRows(null);
    setApplyResult(null);
  };

  return (
    <div className="space-y-4" data-testid="import-page">
      <div className="flex items-center justify-between">
        <Link to="/reconcile" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Import CSV</h1>
        <div className="flex items-center gap-1">
          <Link
            to="/import/history"
            data-testid="import-history-link"
            className="nb-btn nb-btn-secondary !py-2 !px-3"
            title="Import history"
          >
            <History size={16} strokeWidth={3} />
            <span className="hidden sm:inline">History</span>
          </Link>
          <HelpButton id="import" />
        </div>
      </div>

      {accounts.length > 0 && (
        <div
          className="nb-card p-3 space-y-2"
          data-testid="import-account-picker"
          style={{ background: targetAccount?.color || "#fff" }}
        >
          <div className="flex items-center gap-1.5">
            <Wallet size={14} strokeWidth={3} />
            <div className="label !mb-0">Import into which account?</div>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {accounts.map((a) => (
              <button
                key={a.id}
                data-testid={`import-target-${a.name}`}
                onClick={() => setTargetAccountId(a.id)}
                className={`chip whitespace-nowrap flex items-center gap-1.5 ${targetAccountId === a.id ? "active" : ""}`}
                style={targetAccountId === a.id ? { background: a.color, borderColor: "#1a1a1a" } : {}}
              >
                <Wallet size={12} strokeWidth={3} />
                {a.name}
              </button>
            ))}
          </div>
          <div className="text-[11px] font-medium text-[#4a4a4a]">
            All new transactions from this CSV will land in{" "}
            <span className="font-black">{targetAccount?.name || "(pick one)"}</span>.
          </div>
          <label
            className="flex items-start gap-2 pt-2 mt-1 border-t-2 border-[#1a1a1a]/15 cursor-pointer select-none"
            data-testid="auto-clean-toggle-wrap"
          >
            <input
              type="checkbox"
              checked={autoClean}
              onChange={(e) => setAutoClean(e.target.checked)}
              data-testid="auto-clean-toggle"
              className="w-4 h-4 mt-0.5 shrink-0"
            />
            <span className="text-xs font-bold flex items-start gap-1">
              <Sparkles size={12} strokeWidth={3} className="mt-0.5 shrink-0" />
              <span>
                Auto-clean duplicates after import
                <span className="block font-medium text-[#4a4a4a] mt-0.5">
                  Scans this account and offers a one-tap merge for any dupes.
                </span>
              </span>
            </span>
          </label>
        </div>
      )}

      {!rows && (
        <div className="nb-card-lg p-6 text-center">
          <Upload size={40} strokeWidth={2.5} className="mx-auto mb-3" />
          <div className="font-display font-black text-xl mb-1">Upload a bank CSV</div>
          <div className="text-sm text-[#4a4a4a] font-medium mb-4">
            The bank is the source of truth. We&apos;ll clear matching register
            items and add anything new — automatically.
          </div>
          <input
            data-testid="csv-file-input"
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            disabled={busy}
            className="hidden"
            id="csv-input"
          />
          <label
            htmlFor="csv-input"
            data-testid="upload-csv-btn"
            className="nb-btn nb-btn-dark !py-3 inline-flex cursor-pointer"
          >
            <Upload size={18} strokeWidth={3} /> {busy ? "Importing…" : "Choose CSV"}
          </label>
          <div className="text-xs text-[#4a4a4a] font-medium mt-4">
            Expected columns: Date, Description, Amount (or Debit/Credit)
          </div>
        </div>
      )}

      {rows && applyResult && (
        <>
          <div
            className="nb-card p-4 text-sm font-bold space-y-1"
            data-testid="import-result-summary"
          >
            <div className="font-display font-black text-2xl">
              Applied {rows.length} row{rows.length === 1 ? "" : "s"}
              {targetAccount ? ` to ${targetAccount.name}` : ""}
            </div>
            <div>
              <span data-testid="result-cleared">{applyResult.matched}</span>{" "}
              existing txn{applyResult.matched === 1 ? "" : "s"} marked cleared ·{" "}
              <span data-testid="result-created">{applyResult.created}</span>{" "}
              new txn{applyResult.created === 1 ? "" : "s"} added ·{" "}
              <span data-testid="result-skipped">
                {rows.filter((r) => r.duplicate).length}
              </span>{" "}
              already in register
            </div>
          </div>

          <div className="nb-card overflow-hidden">
            {rows.map((r, i) => {
              const outcome = r.duplicate
                ? "already-in-register"
                : r.match_txn_id
                  ? "cleared"
                  : "added";
              const bg =
                outcome === "already-in-register"
                  ? "#e5e7eb"
                  : outcome === "cleared"
                    ? "#b5e4ca"
                    : "#fdecc8";
              const label =
                outcome === "already-in-register"
                  ? "Already in register"
                  : outcome === "cleared"
                    ? "Cleared existing"
                    : "Added new";
              const displayPayee = r.suggested_payee || r.description;
              return (
                <div
                  key={`${r.date}-${r.description}-${r.amount}-${i}`}
                  data-testid={`import-row-${i}`}
                  className="p-3 border-b-2 border-[#1a1a1a]/10 last:border-0"
                  style={{ background: bg }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate flex items-center gap-1.5">
                        {displayPayee || "(no description)"}
                        {r.suggested_payee &&
                          r.suggested_payee !== r.description && (
                            <Sparkles
                              size={12}
                              strokeWidth={3}
                              data-testid={`ai-payee-badge-${i}`}
                              className="text-[#4a4a4a] shrink-0"
                              title={`Bank line: ${r.description}`}
                            />
                          )}
                      </div>
                      <div className="text-xs text-[#4a4a4a] font-medium flex items-center gap-1.5 flex-wrap">
                        <span>{shortDate(r.date)}</span>
                        <span
                          className="nb-tag !text-[9px] !py-0 !px-2"
                          data-testid={`outcome-${i}`}
                        >
                          {label}
                        </span>
                        {outcome === "added" && r.suggested_category && (
                          <span
                            className="nb-tag !text-[9px] !py-0 !px-2 bg-white"
                            data-testid={`cat-tag-${i}`}
                          >
                            <Sparkles
                              size={9}
                              strokeWidth={3}
                              className="inline mr-0.5"
                            />
                            {r.suggested_category}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`font-display font-black text-lg ${r.type === "deposit" ? "balance-pos" : ""}`}
                    >
                      {r.type === "deposit" ? "+" : "-"}
                      {fmtMoney(r.amount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            data-testid="done-import-btn"
            onClick={() => nav("/reconcile")}
            className="nb-btn nb-btn-dark w-full !py-3"
          >
            <Check size={18} strokeWidth={3} /> Done — go to Reconcile
          </button>
          <button
            data-testid="another-file-btn"
            onClick={reset}
            className="nb-btn nb-btn-secondary w-full"
          >
            Import another file
          </button>
        </>
      )}
    </div>
  );
}
